/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { Typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import CoachSkipLink, {
  type Props as CoachSkipLinkProps,
} from 'bundles/ai-coach-platform/components/patterns/chat/common/CoachSkipLink';
import { type CoachSuggestionComponent } from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestion';
import CoachSuggestionsContextProvider from 'bundles/ai-coach-platform/components/patterns/suggestions/context/CoachSuggestionsContextProvider';
import { type SuggestionAction } from 'bundles/ai-coach-platform/components/patterns/suggestions/types';
import { getResolvedSuggestions } from 'bundles/ai-coach-platform/components/patterns/suggestions/utils';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  containerHeader: css`
    position: relative;
    background-color: var(--cds-color-neutral-background-primary-weak) !important;
    padding: var(--cds-spacing-200);
    border-top-right-radius: var(--cds-border-radius-200);
    border-top-left-radius: var(--cds-border-radius-200);
    margin-bottom: var(--cds-spacing-50);

    .suggestion-header-title {
      color: var(--cds-color-grey-700);
    }
  `,
  suggestionsContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);

    .coach-suggestion:last-of-type {
      .disclosure-header[aria-expanded='false'],
      .disclosure {
        border-bottom-left-radius: var(--cds-border-radius-200) !important;
        border-bottom-right-radius: var(--cds-border-radius-200) !important;
      }
    }
  `,
  skipLink: css`
    position: absolute;
    right: var(--cds-spacing-200);
    top: var(--cds-spacing-100);
  `,
};

type Props = {
  /**
   * Callback when the suggestion action is updated
   */
  onAction?: (id: string, action: SuggestionAction) => void;

  /**
   * Callback when one of the suggestion disclosures is toggled
   */
  onDisclosureToggle?: (id: string, expanded: boolean) => void;

  /**
   * custom label to use for the main container's header
   * @default "All suggestions"
   */
  suggestionsHeaderLabel?: string;

  /**
   * Config for a skip link to directly navigate to a particular DOM element of interest.
   * This is useful if there are lots of suggestions and we need a shortcut for keyboard users to skip
   * across all the suggestions.
   *
   * e.g. skip to an "Accept all suggestions" button that might be in another part of the UI, not immediately
   * after the suggestions container.
   *
   * @param targetRef ref of element to move focus to.
   * @param label text label for the skip link.
   */
  skipLink?: CoachSkipLinkProps;

  /**
   * Optional id to control the expanded state
   */
  expandedId?: string | null;

  children: CoachSuggestionComponent[];

  className?: string;

  ariaLabel?: string;

  /**
   * Whether to skip tracking events for the suggestions container.
   * @default false
   */
  skipTracking?: boolean;
};

export const CoachSuggestions = (props: Props): React.ReactElement => {
  const {
    expandedId,
    onDisclosureToggle,
    suggestionsHeaderLabel = _t('All suggestions'),
    skipLink,
    className,
    onAction,
    children,
    ariaLabel,
    skipTracking = false,
  } = props;
  const trackingData = useCoachTrackingContext()?.data;
  const visibilityRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_coach_suggestions', {
    suggestionsCount: children.length,
    suggestionIds: children.map((s) => s.props.id),
    coach: {
      mode: trackingData?.mode ?? 'UNKNOWN_UNDEFINED',
    },
  });

  const numResolved = React.useMemo(() => getResolvedSuggestions(children).length, [children]);

  return (
    <div
      className={classNames('coach-chat-suggestions', className)}
      role="group"
      aria-label={
        ariaLabel ??
        _t('All suggestions, #{numResolved} of #{total} resolved', {
          numResolved,
          total: children.length,
        })
      }
      ref={skipTracking ? undefined : visibilityRef}
    >
      <div css={styles.containerHeader}>
        <Typography2 variant="actionSecondary" component="h3" className="suggestion-header-title">
          {suggestionsHeaderLabel}
        </Typography2>
        {skipLink ? <CoachSkipLink {...skipLink} visibleCss={styles.skipLink} /> : null}
      </div>
      <div className="suggestions-container" css={styles.suggestionsContainer}>
        <CoachSuggestionsContextProvider
          expandedId={expandedId}
          onAction={onAction}
          onDisclosureToggle={onDisclosureToggle}
        >
          {children}
        </CoachSuggestionsContextProvider>
      </div>
    </div>
  );
};

export default CoachSuggestions;
